/**
 * Domain model shared by the browser client and the sync server.
 *
 * Every syncable record carries `updatedAt` (client wall-clock milliseconds) and a
 * nullable `deletedAt`. Replication is last-write-wins on `updatedAt`, so records are
 * never hard-deleted while they still need to propagate.
 */

/** Opaque identifier of a project. Generated client-side so projects can be created offline. */
export type ProjectId = string;

/** Opaque identifier of a person on a project roster. */
export type PersonId = string;

/** Clerk user identifier (`user_...`). */
export type UserId = string;

/** Calendar day in `YYYY-MM-DD` form, interpreted in the viewer's local calendar. */
export type IsoDate = string;

/** Day of the week, `0` = Sunday through `6` = Saturday, matching `Date.prototype.getDay`. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Milliseconds since the Unix epoch. */
export type EpochMillis = number;

/**
 * The recorded attendance of one person at one session.
 *
 * The four states form two pairs: someone was there (attending, late) or was not (missing,
 * excused). The roll-call control is built on that pairing, and so is the present rate —
 * `late` counts as present, because the person was in the room.
 */
export enum AttendanceState {
  Attending = 'attending',
  Late = 'late',
  Excused = 'excused',
  Missing = 'missing',
}

/** Every state a roster row can display, including the absence of a record. */
export type DisplayState = AttendanceState | 'unset';

/**
 * All attendance states in display order, running from fully present to unexplained absence.
 *
 * Bars, legends, and count rows follow this order. It is deliberately not the order repeated
 * presses walk through, which is defined per button by the roll-call control.
 */
export const ATTENDANCE_STATES: readonly AttendanceState[] = [
  AttendanceState.Attending,
  AttendanceState.Late,
  AttendanceState.Excused,
  AttendanceState.Missing,
];

/** Returns true when `value` is one of the four recorded attendance states. */
export function isAttendanceState(value: unknown): value is AttendanceState {
  return value === AttendanceState.Attending
    || value === AttendanceState.Late
    || value === AttendanceState.Excused
    || value === AttendanceState.Missing;
}

/** Permission level of a member on a project. Owners may rename, delete, and manage members. */
export enum MemberRole {
  Owner = 'owner',
  Member = 'member',
}

/** Returns true when `value` is a known member role. */
export function isMemberRole(value: unknown): value is MemberRole {
  return value === MemberRole.Owner || value === MemberRole.Member;
}

/** Fields present on every replicated record. */
export interface Syncable {
  /** Client wall-clock time of the last write; the last-write-wins comparison key. */
  updatedAt: EpochMillis;
  /** Time of the soft delete, or `null` while the record is live. */
  deletedAt: EpochMillis | null;
}

/** A group whose roster is checked for attendance on a recurring schedule. */
export interface Project extends Syncable {
  id: ProjectId;
  name: string;
  /**
   * When the project was created, as epoch milliseconds.
   *
   * This is the floor for session derivation: without it a project with an open term would
   * project its weekday rule indefinitely into the past and report thousands of unrecorded
   * slots that never existed. An explicit `termStart` overrides it, which is how a project
   * created mid-term can still backfill earlier sessions.
   */
  createdAt: EpochMillis;
  /** Weekdays on which a session recurs. Empty means only explicitly added dates are sessions. */
  weekdays: Weekday[];
  /** First day the schedule applies, or `null` for unbounded. */
  termStart: IsoDate | null;
  /** Last day the schedule applies, or `null` for unbounded. */
  termEnd: IsoDate | null;
  /** When true, excused absences count towards the present rate instead of against it. */
  countExcusedAsPresent: boolean;
  /**
   * When true, a link to this project shows its name and roster size in chat previews.
   *
   * Off by default. A preview is rendered for whoever holds the link — including the chat
   * service generating it — with no sign-in, so turning it on is a deliberate disclosure.
   */
  shareLinkPreview: boolean;
}

/** A user's access to a project. Created by email so invitees need not have signed up yet. */
export interface Membership extends Syncable {
  /** Composite key `${projectId}:${lowercased email}`. */
  id: string;
  projectId: ProjectId;
  /** Lowercased invite address; the join key until the invitee signs in. */
  email: string;
  /** Clerk user id once the invitee has signed in, otherwise `null`. */
  userId: UserId | null;
  role: MemberRole;
}

/** Someone on a project roster whose attendance is tracked. */
export interface Person extends Syncable {
  id: PersonId;
  projectId: ProjectId;
  name: string;
  /** Free-text note shown under the name, for example a group or contact detail. */
  note: string;
}

/** How a single date deviates from the project's recurring weekday schedule. */
export enum OverrideKind {
  /** A session on a date the weekday schedule does not cover. */
  Added = 'added',
  /** No session on a date the weekday schedule would otherwise cover. */
  Skipped = 'skipped',
}

/** Returns true when `value` is a known override kind. */
export function isOverrideKind(value: unknown): value is OverrideKind {
  return value === OverrideKind.Added || value === OverrideKind.Skipped;
}

/** A one-off deviation from the recurring schedule. */
export interface SessionOverride extends Syncable {
  /** Composite key `${projectId}:${date}`. */
  id: string;
  projectId: ProjectId;
  date: IsoDate;
  kind: OverrideKind;
}

/** One person's attendance at one session. */
export interface AttendanceMark extends Syncable {
  /** Composite key `${personId}:${date}`, so a re-mark overwrites rather than duplicates. */
  id: string;
  projectId: ProjectId;
  personId: PersonId;
  date: IsoDate;
  state: AttendanceState;
  /**
   * Email address of whoever last set this mark, or an empty string when unknown.
   *
   * Marks are the one record two people edit at the same time — a shared roll call — so the
   * author is kept in order to attribute a change back to them rather than letting one
   * person's tap silently replace another's.
   */
  updatedBy: string;
}

/**
 * Something written down about one session, either about the day or about one person on it.
 *
 * A note is keyed by what it is about rather than by a generated id, so two people writing
 * about the same session converge on one text under last-write-wins instead of accumulating
 * near-duplicate entries. Clearing the text soft-deletes the record, exactly as clearing a
 * mark does.
 */
export interface Note extends Syncable {
  /** Composite key `${projectId}:${date}:${personId ?? ''}`. */
  id: string;
  projectId: ProjectId;
  date: IsoDate;
  /** The person the note is about, or `null` when it is about the session itself. */
  personId: PersonId | null;
  text: string;
  /** Email address of whoever last wrote this note, or an empty string when unknown. */
  updatedBy: string;
}

/** The six replicated entity families, used as the discriminator in sync payloads. */
export enum EntityKind {
  Project = 'project',
  Membership = 'membership',
  Person = 'person',
  SessionOverride = 'sessionOverride',
  AttendanceMark = 'attendanceMark',
  Note = 'note',
}

/** Maps each entity kind to its record type. */
export interface EntityMap {
  [EntityKind.Project]: Project;
  [EntityKind.Membership]: Membership;
  [EntityKind.Person]: Person;
  [EntityKind.SessionOverride]: SessionOverride;
  [EntityKind.AttendanceMark]: AttendanceMark;
  [EntityKind.Note]: Note;
}

/** Builds the composite key of an attendance mark. */
export function attendanceMarkId(personId: PersonId, date: IsoDate): string {
  return `${personId}:${date}`;
}

/** Builds the composite key of a session override. */
export function sessionOverrideId(projectId: ProjectId, date: IsoDate): string {
  return `${projectId}:${date}`;
}

/**
 * Builds the composite key of a note.
 *
 * A day note and a person's note on the same day differ only in the last segment, which is
 * empty for the day note.
 */
export function noteId(projectId: ProjectId, date: IsoDate, personId: PersonId | null): string {
  return `${projectId}:${date}:${personId ?? ''}`;
}

/** Builds the composite key of a membership. */
export function membershipId(projectId: ProjectId, email: string): string {
  return `${projectId}:${email.trim().toLowerCase()}`;
}
